import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Tooltip,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import dayjs from "dayjs";
import { utcToLocal, localToUtc } from "../../Utils/timeConversions";
import { NumericFormat } from "react-number-format";
import { inputStyle } from "./styles";
import { fieldOptions } from "./optionsHelper";
import {
  MKTX_DEALER_LIST_MODES,
  MKTX_INQUIRY_TYPES,
  MKTX_PRICE_TYPES,
  PROTOCOL_TYPES,
  MKTX_PRICING_PROCESSES,
  MKTX_PRICING_PROCESS_DISPLAY,
  MKTX_STRATEGY_TYPES,
  MKTX_DEFAULT_EXPIRY_TIME_UTC,
} from "../../Constants/AppConstants";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  pricingProcessInfoContent,
} from "./MarketAxessInfoContent";
import { tokens } from "../../Common/designTokens";

// Helper to get the default expiry time UTC of the current day
// Time is driven by the MKTX_DEFAULT_EXPIRY_TIME_UTC constant (HH:MM:SS.mmm)
const getDefaultExpiryTime = () => {
  const [timePart] = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".");
  const [hh, mm, ss] = timePart.split(":").map(Number);
  const msStr = (MKTX_DEFAULT_EXPIRY_TIME_UTC || "20:57:00.000").split(".")[1] || "0";
  const ms = Number(msStr);
  const now = new Date();
  const utcDate = new Date(Date.UTC(
    now.getUTCFullYear(),
    now.getUTCMonth(),
    now.getUTCDate(),
    hh,
    mm,
    ss,
    ms
  ));
  // Return ISO string but remove the 'Z' suffix so it's treated as UTC without timezone conversion
  return utcDate.toISOString().slice(0, -1);
};

const DEFAULT_EFFECTIVE_OFFSET_MS = 5 * 60 * 1000; // 5 minutes

const parseUtcMs = (val) => {
  if (!val) return NaN;
  try {
    const str = String(val).trim();
    const withZ = /[zZ]|[+\-]\d{2}:\d{2}$/.test(str) ? str : `${str}Z`;
    const d = new Date(withZ);
    return isNaN(d.getTime()) ? NaN : d.getTime();
  } catch (err) {
    console.error("parseUtcMs error:", err, val);
    return NaN;
  }
};

const getDefaultEffectiveFromExpiry = (expiryIso) => {
  // Prefill only: use the earlier of current UTC time and expiry (9:30 PM UTC default)
  const now = new Date();
  const nowMs = now.getTime();
  const expiryMs = parseUtcMs(expiryIso);
  const effectiveMs = Number.isFinite(expiryMs) ? Math.min(nowMs, expiryMs) : nowMs;
  return new Date(effectiveMs).toISOString().slice(0, -1);
};

const pricingTooltipStyles = {
  tooltip: {
    bgcolor: tokens.colors.darkgray,
    color: tokens.colors.white,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.25)",
    fontSize: "0.85rem",
    maxWidth: 320,
    p: 1,
  },
  arrow: {
    color: tokens.colors.darkgray,
  },
};

const strategyTooltipStyles = {
  tooltip: {
    bgcolor: tokens.colors.darkgray,
    color: tokens.colors.white,
    border: `1px solid ${tokens.colors.bodyBorderColor50}`,
    boxShadow: "0px 10px 30px rgba(0, 0, 0, 0.25)",
    fontSize: "0.85rem",
    maxWidth: 620,
    p: 1,
  },
  arrow: {
    color: tokens.colors.darkgray,
  },
};

const MarketAxessFields = ({
  actualOrderData,
  handleChange,
  errors,
  isFieldEditable,
  clearFieldError,
  clearModalError,
  setActualOrderData,
  venueOptions = [],
  handleVenueChange,
  authToken,
  readOnly,
}) => {
  const safeErrors = readOnly ? {} : errors || {};

  // Ensure rfqInquiryType is BIN for MarketAxess
  React.useEffect(() => {
    if (
      actualOrderData &&
      actualOrderData.protocol === PROTOCOL_TYPES.MARKETAXESS &&
      actualOrderData.rfqInquiryType !== "BIN"
    ) {
      setActualOrderData && setActualOrderData({ rfqInquiryType: MKTX_INQUIRY_TYPES.BIN });
    }
  }, [actualOrderData, setActualOrderData]);

  // NEW: local state to hold a snapshot of effectiveTime used only for UTC comparison/disable logic.
  // Initialize from store if present, but do NOT overwrite it later unless it's null.
  const [effectiveTimeValue, setEffectiveTimeValue] = React.useState(
    actualOrderData?.effectiveTime ?? null
  );

  // Sync once: if store provides an effectiveTime and our local snapshot is null, capture it.
  React.useEffect(() => {
    if (effectiveTimeValue == null && actualOrderData?.effectiveTime) {
      setEffectiveTimeValue(actualOrderData.effectiveTime);
    }
  }, [actualOrderData?.effectiveTime, effectiveTimeValue]);

  // SKIP in readOnly mode OR edit RFQ mode to preserve original row data
  React.useEffect(() => {
    if (
      readOnly ||
      actualOrderData?.editRfqMode ||
      !actualOrderData ||
      actualOrderData.protocol !== PROTOCOL_TYPES.MARKETAXESS
    ) {
      return;
    }

    const defaultExpiryTime = getDefaultExpiryTime();
    const defaultEffectiveTime = getDefaultEffectiveFromExpiry(defaultExpiryTime);
    const updates = {};
    if (!actualOrderData.effectiveTime) {
      updates.effectiveTime = defaultEffectiveTime;
      // Only set the local snapshot if it is currently null
      if (effectiveTimeValue == null) {
        setEffectiveTimeValue(defaultEffectiveTime);
      }
    }
    if (!actualOrderData.expiryTime) {
      updates.expiryTime = defaultExpiryTime;
    }
    if (Object.keys(updates).length > 0) {
      setActualOrderData && setActualOrderData(updates);
    }
  }, [
    actualOrderData?.protocol,
    actualOrderData?.effectiveTime,
    actualOrderData?.expiryTime,
    actualOrderData?.editRfqMode,
    setActualOrderData,
    readOnly,
    effectiveTimeValue,
  ]);

  const handleMktxChange = (event) => {
    const { name, value } = event.target;

    if (value !== undefined && value !== "") {
      clearFieldError && clearFieldError(name);
      clearModalError && clearModalError(name);
    }

    handleChange(event);
  };

  const handleNumericChange = (name, values) => {
    const { floatValue, value } = values;
    handleMktxChange({
      target: {
        name,
        value: value === "" ? "" : floatValue,
      },
    });
  };

  const handleDateChange = (name, value) => {
    handleMktxChange({
      target: {
        name,
        // Convert local picker value back to UTC before storing
        value: localToUtc(value),
      },
    });
  };

  const futureMin = dayjs(); // local now — consistent with local-mode pickers

  // Derive local dayjs objects for boundary computations
  const localEffectiveForBounds = utcToLocal(actualOrderData.effectiveTime);
  const localExpiryForBounds = utcToLocal(actualOrderData.expiryTime ?? actualOrderData.rfqExpiryTime);

  // Expiry picker min: must be >= now AND >= effective time (whichever is later)
  const expiryMin =
    localEffectiveForBounds && localEffectiveForBounds.isAfter(futureMin)
      ? localEffectiveForBounds
      : futureMin;

  const showDelayedFields = true;

  // NEW: Never disable in edit RFQ mode
  const isEditRfqMode = actualOrderData?.editRfqMode === true;

  // Determine if in edit-RFQ mode and effectiveTime (snapshot) is in the past or equal (UTC)
  // But never actually disable if in edit RFQ mode
  const effectiveMs = parseUtcMs(effectiveTimeValue);
  const nowUtc = Date.now();
  const disableEffectiveDueToPast = !isEditRfqMode && isEditRfqMode && Number.isFinite(effectiveMs) && effectiveMs <= nowUtc;

  const pricingProcessValue = actualOrderData?.mktxPricingProcess;
  const pricingProcessOptions = React.useMemo(() => {
    const base = fieldOptions.mktxPricingProcess || [];
    if (!pricingProcessValue) return base;
    return base.includes(pricingProcessValue) ? base : [...base, pricingProcessValue];
  }, [pricingProcessValue]);
  const pricingProcessDisplayValue = pricingProcessValue ?? pricingProcessOptions[0] ?? "";

  // NEW: Check if strategy type is SEEK or PROVIDE (dealer list mode should be hidden)
  const isSeekOrProvide =
    actualOrderData?.strategyType === MKTX_STRATEGY_TYPES.SEEK ||
    actualOrderData?.strategyType === MKTX_STRATEGY_TYPES.PROVIDE;

  // Check if effective > expiry for error display
  const effMs = parseUtcMs(actualOrderData.effectiveTime);
  const expMs = parseUtcMs(actualOrderData.expiryTime);
  const hasTimeError = Number.isFinite(effMs) && Number.isFinite(expMs) && effMs > expMs;

  return (
    <>
      {/* Dealer List Mode - Hidden when strategy type is SEEK or PROVIDE */}
      <Grid item xs={3} data-testid="dealer-list-mode-grid" sx={{ display: isSeekOrProvide ? 'none' : 'block' }}>
        <TextField
          fullWidth
          select
          name="mktxDealerListMode"
          value={actualOrderData.mktxDealerListMode || ""}
          onChange={handleMktxChange}
          variant="filled"
          label="Dealer List Mode"
          error={errors?.mktxDealerListMode}
          disabled={!isFieldEditable("mktxDealerListMode")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          {fieldOptions.mktxDealerListMode.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Inquiry Type - MarketAxess: always BIN (display only, disabled) */}
      <Grid item xs={3}>
        <TextField
          fullWidth
          name="rfqInquiryType"
          value={MKTX_INQUIRY_TYPES?.BIN || "BIN"}
          variant="filled"
          label="Inquiry Type"
          error={errors?.rfqInquiryType}
          disabled
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        />
      </Grid>

      {/* Price Type - uses shared priceType key, default to SPREAD */}
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="priceType"
          value={actualOrderData.priceType || MKTX_PRICE_TYPES.SPREAD}
          onChange={handleMktxChange}
          variant="filled"
          label="Price Type"
          error={errors?.priceType}
          disabled={!isFieldEditable("priceType")}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
        >
          {/* MarketAxess supports PRICE, SPREAD, and YIELD */}
          {fieldOptions.mktxPriceType.map((option) => (
            <MenuItem key={option} value={option}>
              {option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Target Level */}
      <Grid item xs={3}>
        <NumericFormat
          customInput={TextField}
          name="mktxTargetLevel"
          value={actualOrderData.mktxTargetLevel ?? ""}
          variant="filled"
          fullWidth
          label="Target Level"
          placeholder="Enter target level"
          allowNegative={false}
          decimalScale={6}
          disabled={!isFieldEditable("mktxTargetLevel")}
          error={errors?.mktxTargetLevel}
          sx={inputStyle}
          InputProps={{ disableUnderline: true }}
          onValueChange={(values) => handleNumericChange("mktxTargetLevel", values)}
        />
      </Grid>

      {/* New: Pricing Process */}
      <Grid item xs={3}>
        <TextField
          fullWidth
          select
          name="mktxPricingProcess"
          value={pricingProcessDisplayValue}
          onChange={handleMktxChange}
          variant="filled"
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Pricing Process
              <Tooltip
                title={pricingProcessInfoContent}
                arrow
                componentsProps={{
                  tooltip: { sx: pricingTooltipStyles.tooltip },
                  arrow: { sx: pricingTooltipStyles.arrow },
                }}
              >
                <InfoOutlinedIcon
                  fontSize="small"
                  sx={{
                    cursor: 'pointer',
                  }}
                />
              </Tooltip>
            </Box>
          }
          error={errors?.mktxPricingProcess}
          disabled={!isFieldEditable("mktxPricingProcess")}
          sx={inputStyle}
          InputProps={{
            disableUnderline: true,
          }}
        >
          {pricingProcessOptions.map((option) => (
            <MenuItem key={`pricing-${option}`} value={option}>
              {MKTX_PRICING_PROCESS_DISPLAY[option] || option}
            </MenuItem>
          ))}
        </TextField>
      </Grid>

      {/* Effective Time */}
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Effective Time "
            value={utcToLocal(actualOrderData.effectiveTime)}
            onChange={(v) => handleDateChange("effectiveTime", v)}
            minDateTime={futureMin}
            maxDateTime={localExpiryForBounds ?? undefined}
            closeOnSelect={false}
            // Never disable in edit RFQ mode
            disabled={!isFieldEditable("effectiveTime") && !isEditRfqMode}
            slotProps={{
              textField: {
                variant: "filled",
                fullWidth: true,
                error: !readOnly && hasTimeError,
                disabled: !isFieldEditable("effectiveTime") && !isEditRfqMode,
                sx: {
                  ...inputStyle,
                  '& .MuiInputAdornment-root': {
                    marginLeft: '-8px',
                  },
                },
                InputProps: { disableUnderline: true },
              },
              popper: {
                placement: "auto",
              },
            }}
          />
        </LocalizationProvider>
      </Grid>

      {/* Expiry Time */}
      <Grid item xs={3}>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DateTimePicker
            label="Expiry Time "
            value={utcToLocal(actualOrderData.expiryTime ?? actualOrderData.rfqExpiryTime)}
            onChange={(v) => handleDateChange("expiryTime", v)}
            minDateTime={expiryMin}
            closeOnSelect={false}
            // Never disable in edit RFQ mode
            disabled={!isFieldEditable("expiryTime") && !isEditRfqMode}
            slotProps={{
              textField: {
                variant: "filled",
                fullWidth: true,
                error: !readOnly && hasTimeError,
                disabled: !isFieldEditable("expiryTime") && !isEditRfqMode,
                sx: {
                  ...inputStyle,
                  '& .MuiInputAdornment-root': {
                    marginLeft: '-8px',
                  },
                },
                InputProps: { disableUnderline: true },
              },
              popper: {
                placement: "auto",
              },
            }}
          />
        </LocalizationProvider>
      </Grid>
    </>
  );
};

export default MarketAxessFields;
